/**
 * Agencies API
 *
 * Aggregates agency data from the deduplicated properties table.
 * No dedicated agencies table exists yet -- all queries derive from
 * `properties.agency_name`.
 */

import { supabase } from '@/integrations/supabase/client';

// ============================================================
// Types
// ============================================================

export interface AgencySummary {
  agency_name: string;
  property_count: number;
  states: string[];
  listing_types: string[];
  last_seen_at: string | null;
}

export interface AgencyFilters {
  search?: string;
  state?: string;
}

export interface AgencyStats {
  uniqueAgencies: number;
  propertiesWithAgency: number;
  totalProperties: number;
}

// ============================================================
// Queries
// ============================================================

const AGENCY_PAGE_SIZE = 50;

/**
 * Fetch aggregated agency summaries from the properties table.
 *
 * Since Supabase JS SDK lacks GROUP BY, we fetch the minimal
 * projection and aggregate client-side. The `properties` table
 * currently has ~6 k rows so this is acceptable for a stub.
 */
export async function fetchAgencies(options: {
  filters?: AgencyFilters;
  limit?: number;
  offset?: number;
  sortBy?: 'property_count' | 'agency_name' | 'last_seen_at';
  sortOrder?: 'asc' | 'desc';
} = {}): Promise<{ data: AgencySummary[]; total: number }> {
  const {
    filters = {},
    limit = AGENCY_PAGE_SIZE,
    offset = 0,
    sortBy = 'property_count',
    sortOrder = 'desc',
  } = options;

  let query = (supabase as any)
    .from('properties')
    .select('agency_name, state, listing_type, last_scraped_at')
    .not('agency_name', 'is', null);

  if (filters.state && filters.state !== 'all') {
    query = query.eq('state', filters.state);
  }

  const { data: rows, error } = await query;

  if (error) {
    console.error('Error fetching agency data:', error);
    throw error;
  }

  if (!rows || rows.length === 0) {
    return { data: [], total: 0 };
  }

  type Row = { agency_name: string; state: string | null; listing_type: string | null; last_scraped_at: string | null };

  const map = new Map<string, {
    property_count: number;
    states: Set<string>;
    listing_types: Set<string>;
    last_seen_at: string | null;
  }>();

  for (const row of rows as Row[]) {
    const name = row.agency_name;
    let entry = map.get(name);
    if (!entry) {
      entry = { property_count: 0, states: new Set(), listing_types: new Set(), last_seen_at: null };
      map.set(name, entry);
    }
    entry.property_count += 1;
    if (row.state) entry.states.add(row.state);
    if (row.listing_type) entry.listing_types.add(row.listing_type);
    if (row.last_scraped_at && (!entry.last_seen_at || row.last_scraped_at > entry.last_seen_at)) {
      entry.last_seen_at = row.last_scraped_at;
    }
  }

  let summaries: AgencySummary[] = [...map.entries()].map(([name, v]) => ({
    agency_name: name,
    property_count: v.property_count,
    states: [...v.states].sort(),
    listing_types: [...v.listing_types].sort(),
    last_seen_at: v.last_seen_at,
  }));

  if (filters.search) {
    const term = filters.search.toLowerCase();
    summaries = summaries.filter((a) => a.agency_name.toLowerCase().includes(term));
  }

  const compareFn = (a: AgencySummary, b: AgencySummary): number => {
    const dir = sortOrder === 'asc' ? 1 : -1;
    if (sortBy === 'property_count') return (a.property_count - b.property_count) * dir;
    if (sortBy === 'agency_name') return a.agency_name.localeCompare(b.agency_name) * dir;
    if (sortBy === 'last_seen_at') {
      const aDate = a.last_seen_at ?? '';
      const bDate = b.last_seen_at ?? '';
      return aDate.localeCompare(bDate) * dir;
    }
    return 0;
  };
  summaries.sort(compareFn);

  const total = summaries.length;
  const page = summaries.slice(offset, offset + limit);

  return { data: page, total };
}

/**
 * High-level stats about agency coverage.
 */
export async function getAgencyStats(): Promise<AgencyStats> {
  const [totalResult, withAgencyResult] = await Promise.all([
    (supabase as any).from('properties').select('*', { count: 'exact', head: true }),
    (supabase as any).from('properties').select('*', { count: 'exact', head: true }).not('agency_name', 'is', null),
  ]);

  const { data: distinctRows, error } = await (supabase as any)
    .from('properties')
    .select('agency_name')
    .not('agency_name', 'is', null);

  let uniqueAgencies = 0;
  if (!error && distinctRows) {
    uniqueAgencies = new Set((distinctRows as { agency_name: string }[]).map((r) => r.agency_name)).size;
  }

  return {
    uniqueAgencies,
    propertiesWithAgency: withAgencyResult.count ?? 0,
    totalProperties: totalResult.count ?? 0,
  };
}

/**
 * Fetch properties for a single agency name (detail view).
 */
export async function getAgencyDetail(agencyName: string): Promise<{
  agency_name: string;
  properties: Array<{
    id: string;
    full_address: string;
    suburb: string;
    state: string;
    property_type: string | null;
    listing_type: string | null;
    price: number | null;
    price_display: string | null;
    last_scraped_at: string | null;
  }>;
}> {
  const { data, error } = await (supabase as any)
    .from('properties')
    .select('id, full_address, suburb, state, property_type, listing_type, price, price_display, last_scraped_at')
    .eq('agency_name', agencyName)
    .order('last_scraped_at', { ascending: false });

  if (error) {
    console.error('Error fetching agency detail:', error);
    throw error;
  }

  return { agency_name: agencyName, properties: data ?? [] };
}
